import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { IonosLogo } from '@/modules/user-feedback/components/ionos-logo';
import { Button } from '@/ui/components/ui/button';
import { Card, CardContent } from '@/ui/components/ui/card';
import { Input } from '@/ui/components/ui/input';
import { Label } from '@/ui/components/ui/label';
import { useAdminAuth } from '../context/AdminAuthContext';

export function AdminLoginPage() {
	const { login, user, loading } = useAdminAuth();
	const location = useLocation();
	const justRegistered = (location.state as { registered?: boolean } | null)
		?.registered;

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);

	if (!loading && user) {
		return <Navigate to='/admin' replace />;
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setSubmitting(true);
		try {
			await login(email, password);
		} catch {
			setError('Invalid email or password');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className='relative min-h-screen bg-background flex items-center justify-center px-4'>
			<div className='w-full max-w-sm'>
				<div className='mb-10 flex flex-col items-center gap-4'>
					<IonosLogo />
					<div className='text-center'>
						<h1 className='text-2xl font-semibold text-foreground tracking-tight'>
							Feature Request
						</h1>
						<p className='mt-1 text-sm text-muted-foreground'>
							Sign in to manage projects and feedback
						</p>
					</div>
				</div>

				<Card>
					<CardContent className='pt-6'>
						{justRegistered && (
							<p className='mb-4 text-sm text-green-700 rounded-xl bg-green-50 px-3.5 py-2.5'>
								Account created — you can now sign in.
							</p>
						)}

						<form
							onSubmit={handleSubmit}
							className='flex flex-col gap-4'
						>
							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='email'>Email</Label>
								<Input
									id='email'
									type='email'
									autoComplete='email'
									required
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder='admin@example.com'
								/>
							</div>

							<div className='flex flex-col gap-1.5'>
								<Label htmlFor='password'>Password</Label>
								<Input
									id='password'
									type='password'
									autoComplete='current-password'
									required
									value={password}
									onChange={(e) =>
										setPassword(e.target.value)
									}
									placeholder='••••••••'
								/>
							</div>

							{error && (
								<p className='text-sm text-destructive rounded-xl bg-destructive/10 px-3.5 py-2.5'>
									{error}
								</p>
							)}

							<Button
								type='submit'
								disabled={submitting}
								className='mt-1 w-full'
							>
								{submitting ? 'Signing in…' : 'Sign in'}
							</Button>
						</form>
					</CardContent>
				</Card>

				<p className='mt-4 text-center text-sm text-muted-foreground'>
					Don't have an account?{' '}
					<Link
						to='/admin/register'
						className='text-primary hover:underline font-medium'
					>
						Create one
					</Link>
				</p>
			</div>

			<p className='absolute bottom-6 left-0 right-0 text-center text-sm text-muted-foreground'>
				<Link
					to='/feature-request'
					className='text-primary hover:underline font-medium'
				>
					Request a feature
				</Link>
			</p>
		</div>
	);
}
